// OrbitalReg — Saturn silhouette with a cyan/violet ring.
// Wordmark renders ORBITAL/REG in uppercase tracking — mission-
// control aesthetic. Body-copy uses CamelCase "OrbitalReg".
//
// v184 — Wordmark falls back to the operator-uploaded logo from
// /branding/logo when one is configured (visible via the public-
// config endpoint). The configured logo replaces both the SVG
// glyph and the "ORBITAL / REG" text wordmark in the AppShell
// header — operators upload a single logo with whatever proportions
// suit their brand.

import { usePublicConfig } from '../api/publicConfig';

interface Props {
  readonly className?: string;
  readonly size?: number;
}

export function Logo({ className, size = 24 }: Props) {
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Canonical OrbitalReg brand mark: a planet on a tilted elliptical
          orbit with the cyan→violet brand gradient. Matches orbital-mark.svg
          used across the marketing site, docs and email templates. */}
      <defs>
        <linearGradient id="orbital-ring-grad" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#6ee7ff" />
          <stop offset="1" stopColor="#a78bfa" />
        </linearGradient>
      </defs>
      <ellipse
        cx="32"
        cy="32"
        rx="27"
        ry="15"
        fill="none"
        stroke="url(#orbital-ring-grad)"
        strokeWidth="3.5"
        transform="rotate(-22 32 32)"
      />
      <circle cx="9" cy="39" r="4" fill="#6ee7ff" />
    </svg>
  );
}

// Wordmark — "ORBITAL/REG" in uppercase tracked display type.
// The slash echoes the URL-namespace shape this product lives in.
// `compact` shrinks to logo + slash, used in tight spaces.
interface WordmarkProps {
  readonly className?: string;
  readonly compact?: boolean;
}

export function Wordmark({ className, compact = false }: WordmarkProps) {
  // v184 — operator-uploaded logo overrides the built-in mark.
  // We fetch the public-config (cached aggressively per session); a
  // non-empty logo_url triggers the custom-logo render path below.
  const { data: cfg } = usePublicConfig();
  const customLogo = cfg?.logo_url;

  if (customLogo) {
    return (
      <span
        className={`flex items-center ${className ?? ''}`}
        aria-label="OrbitalReg"
      >
        <img
          src={customLogo}
          alt=""
          aria-hidden="true"
          className="h-7 max-w-[180px] object-contain"
        />
      </span>
    );
  }

  if (compact) {
    return (
      <span className={`flex items-center gap-2 ${className ?? ''}`}>
        <Logo size={22} />
      </span>
    );
  }
  return (
    <span
      className={`flex items-center gap-2.5 font-display ${className ?? ''}`}
      aria-label="OrbitalReg"
    >
      <Logo size={22} />
      <span className="text-fg font-semibold tracking-[0.18em] text-[15px]">
        ORBITAL
      </span>
      <span className="text-accent font-semibold text-[15px] -mx-1.5" aria-hidden="true">
        /
      </span>
      <span className="text-fg-muted font-semibold tracking-[0.18em] text-[15px]">
        REG
      </span>
    </span>
  );
}
